import { ApolloClient, InMemoryCache, ApolloLink, HttpLink, from } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { getStoredToken } from '@/utils/authStorage.js';
import { createIdempotencyKey } from '@/utils/idempotency.js';

const errorSubscribers = new Set();

export function subscribeToApolloErrors(listener) {
  errorSubscribers.add(listener);
  return () => errorSubscribers.delete(listener);
}

function notifyApolloError(payload) {
  errorSubscribers.forEach(listener => listener(payload));
}

const httpLink = new HttpLink({
  uri:
    import.meta.env.VITE_GRAPHQL_URL ||
    `${import.meta.env.VITE_API_URL || 'https://api.sinamonsoft.ar'}/graphql`,
  credentials: 'include',
});

function buildAuthHeaders() {
  const headers = {};

  try {
    const token = getStoredToken();
    const tenantRaw = window.localStorage.getItem('tenant');
    const userRaw = window.localStorage.getItem('user');

    if (token) {
      headers.authorization = `Bearer ${token}`;
    }

    if (tenantRaw) {
      try {
        const tenant = JSON.parse(tenantRaw);
        const tenantHeader = tenant?.code || tenant?.dominio || tenant?.domain || null;
        if (tenantHeader) {
          headers['X-Tenant'] = tenantHeader;
        }
        if (tenant?.id) {
          headers['X-Empresa-Id'] = tenant.id;
        }
      } catch {
        headers['X-Tenant'] = tenantRaw;
      }
    }

    if (userRaw) {
      try {
        const user = JSON.parse(userRaw);
        if (user?.id) {
          headers['X-User-Id'] = user.id;
        }
        const sucursalId = user?.sucursal_activa_id || user?.sucursalActiva?.id || user?.sucursal?.id || user?.sucursal_id;
        if (sucursalId) {
          headers['X-Sucursal-Id'] = sucursalId;
        }
      } catch {
        // noop
      }
    }
  } catch {
    // noop
  }

  return headers;
}

function buildMutationHeaders(operation) {
  const isMutation = operation?.query?.definitions?.some(
    (definition) => definition?.kind === 'OperationDefinition' && definition?.operation === 'mutation',
  );

  if (!isMutation) {
    return {};
  }

  return {
    'x-idempotency-key': createIdempotencyKey(operation?.operationName ? `mutation-${operation.operationName}` : 'mutation'),
  };
}

const authLink = new ApolloLink((operation, forward) => {
  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      ...buildAuthHeaders(),
      ...buildMutationHeaders(operation),
    },
  }));
  return forward(operation);
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors?.length) {
    graphQLErrors.forEach(({ message }) => {
      notifyApolloError({ type: 'error', message: message || 'Error desconocido en GraphQL' });
    });
  }
  if (networkError) {
    notifyApolloError({
      type: 'error',
      message: 'Problema de red al comunicarse con GraphQL.',
    });
  }
});

const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        empleados: {
          keyArgs: ['filter', 'sort'],
          merge(existing, incoming) {
            return incoming;
          },
        },
      },
    },
    Empleado: {
      keyFields: ['id'],
    },
  },
});

export const apolloClient = new ApolloClient({
  link: from([errorLink, authLink, httpLink]),
  cache,
});

export default apolloClient;
